import { Alert, Heading, Text } from "@idg-web-platform/pulse";
import css from "./clubPage.module.css";
import { NavigationWrapper } from "../components/navigation/navigationWrapper.tsx";
import { PointBalanceCard } from "../components/pointBalanceCard/pointBalanceCard.tsx";
import { analyticsContentViewed } from "../utils/analyticsContentViewed.ts";
import { useEffect } from "preact/hooks";
import { ClubCard } from "../components/clubCard/clubCard.tsx";
import { WalletCard } from "../components/walletCard/walletCard.tsx";
import { ApprovalBox } from "./approvalBox.tsx";
import { ApprovalRequiredBox } from "./approvalRequiredBox.tsx";
import { AlreadyLinkedBox } from "./alreadyLinkedBox.tsx";

ClubPage.tagName = "control-club-page";

export function ClubPage(props: ClubPageProps) {
  useEffect(() => {
    analyticsContentViewed("club");
  }, []);

  const active = props.state === ClubMembershipState.Active;
  const hasAnyCode = !!(props.cardCode || props.appCode);
  // ApprovalRequired and ApprovalRequested get different boxes on purpose: only in
  // ApprovalRequested has an approval email actually been sent, so only there can it be resent.
  const approvalRequested =
    props.linkExistingClubEnabled && props.state === ClubMembershipState.ApprovalRequested;
  const approvalRequired =
    props.linkExistingClubEnabled && props.state === ClubMembershipState.ApprovalRequired;
  const alreadyLinked =
    props.linkExistingClubEnabled && props.state === ClubMembershipState.AlreadyLinked;

  function nonActiveDescription(): string {
    switch (props.state) {
      case ClubMembershipState.None:
        return "Du hast aktuell keine CLUB-Mitgliedschaft. Werde Teil des CLUBs und profitiere von exklusiven Vorteilen!";
      case ClubMembershipState.ApprovalRequired:
      case ClubMembershipState.ApprovalRequested:
      case ClubMembershipState.AlreadyLinked:
        return "Für diese E-Mail Adresse besteht bereits eine CLUB-Mitgliedschaft. Bitte kontaktiere den Kundenservice um diese in deinem Kundenkonto anzuzeigen.";
      case ClubMembershipState.Pending:
        return "Deine CLUB-Mitgliedschaft wird gerade bearbeitet. Wir informieren dich, sobald alles abgeschlossen ist.";
      default:
        return "Wir konnten deine CLUB-Mitgliedschaft gerade nicht anzeigen. Bei Fragen, kontaktiere unseren Kundenservice.";
    }
  }

  return (
    <>
      <NavigationWrapper currentPath="/c/club">
        <div className={css.root} data-state={props.stateDebugInfo}>
          <Heading class={css.heading} sizeStep={2}>
            CLUB
          </Heading>

          <Text element="p" sizeStep={0} class={css.subHeadline}>
            Alle Informationen zu deiner CLUB-Mitgliedschaft.
          </Text>

          {active && (
            <div className={css.clubCardContainer}>
              <PointBalanceCard
                pointBalance={props.pointBalance}
                exchangeRate={props.exchangeRate}
                linkTo="/c/club/history"
              />
              <ClubCard
                active={active}
                hasAnyCode={hasAnyCode}
                firstName={props.firstName}
                lastName={props.lastName}
                state={props.state}
                appCode={props.appCode}
                appCodeImage={props.appCodeImage}
                cardCode={props.cardCode}
                cardCodeImage={props.cardCodeImage}
              />
              {hasAnyCode && (props.walletEnabled || props.qrSignedUrlEnabled) && (
                <WalletCard
                  walletQrCodeImage={props.walletQrCodeImage}
                  appleWalletEnabled={props.appleWalletEnabled}
                  googleWalletEnabled={props.googleWalletEnabled}
                  qrSignedUrlEnabled={props.qrSignedUrlEnabled}
                />
              )}
            </div>
          )}

          {!active && !approvalRequested && !approvalRequired && !alreadyLinked && (
            <Alert
              type="info"
              headline="Status deiner CLUB-Mitgliedschaft"
              showCloseButton={false}
              description={nonActiveDescription()}
            />
          )}

          {approvalRequested && <ApprovalBox />}
          {approvalRequired && <ApprovalRequiredBox />}
          {alreadyLinked && <AlreadyLinkedBox />}
        </div>
      </NavigationWrapper>
    </>
  );
}

export enum ClubMembershipState {
  None, //  No club membership at all
  Pending, // Club membership is created
  ApprovalRequired, // Club membership cannot be created: the Email address is in use
  ApprovalRequested, // Customer has received an Email to confirm the club membership
  Active, // maybe still preliminary but valid club membership
  AlreadyLinked, // Approval required, but the existing club membership belongs to another account
}

export interface ClubPageProps {
  linkExistingClubEnabled: boolean;
  state: ClubMembershipState;
  pointBalance: number;
  exchangeRate: number;
  stateDebugInfo?: string;
  firstName: string;
  lastName: string;
  appCode?: string;
  cardCode?: string;
  appCodeImage?: string;
  cardCodeImage?: string;
  walletEnabled?: boolean;
  walletQrCodeImage?: string;
  appleWalletEnabled?: boolean;
  googleWalletEnabled?: boolean;
  qrSignedUrlEnabled?: boolean;
}
